/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useEffect } from 'react'
import { observer } from 'mobx-react'
import useRootStore from '@store/useRootStore'
import PreMatchList from '@views/Matchup/MatchField/PreMatchPage/PreMatchList'
import { GAME_TYPE } from '@game-type/game-market-types'
import { ACCOUNT_TYPE, PAGE_TYPE } from '@interface/common'
import { CLUB_STATE } from 'adv-protocol/src/GameCommon'
import { isViewPage } from '@utils/utils'
import { TournamentTrack } from '@store/matchupStore'

interface PreMatchPageProps {
  pageType?: PAGE_TYPE
  onFieldClickCheck: () => boolean
  tracks?: TournamentTrack[]
}

let intervalObj

const PreMatchPage: React.FC<PreMatchPageProps> = (props: PreMatchPageProps) => {
  const {
    matchupStore,
    globalStore,
    routerStore,
    userStore,
    authStore,
  } = useRootStore()
  const {
    currentGameKey,
    currentSportsKey,
    majorUniqueID,
    club = {},
    isAvailableLiveMatch,
    isLoading,
    selectedDetailTabs,
  } = matchupStore
  const scoreModeEnabled = globalStore.getScoreModeEnabled()
  const { id } = club
  const { onFieldClickCheck, pageType, tracks } = props

  useEffect((): void => {
    if (isViewPage(PAGE_TYPE.MATCHUP)
      && authStore.isSignedIn
      && authStore.accountInfo?.sign === ACCOUNT_TYPE.KAKAO) {
      const userPromiseId = authStore.userInfo.id
      userStore.showAccountTransferInfo(userPromiseId, ACCOUNT_TYPE.KAKAO).catch(console.error)
    }
  }, [pageType, currentGameKey])

  useEffect((): (() => void) => {
    if (!isViewPage(PAGE_TYPE.MATCHUP)
      && !isViewPage(PAGE_TYPE.MATCHUP_RESULT)
      && !isViewPage(PAGE_TYPE.MATCHUP_RESULT_FILTER)) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {
      }
    }

    if (pageType === PAGE_TYPE.MATCHUP_FILTER) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {}
    }

    if (pageType === PAGE_TYPE.MATCHUP_RESULT && isLoading) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {}
    }

    const pathName = routerStore.location.pathname
    // 경기결과 페이지에서 뒤로가기 routerStore.goBack() 을 하면 url 이 변경되고 이전 페이지가 호출되는데
    // 메인 매치업이 이전 페이지 였을 경우 pageType 값 초기화 전 pageType 은 MATCHUP_RESULT 이면서 pathName 은 cross 이다.
    // 이때 기존 조건으로는 pathName 이 matchupResult 일때만 건너띄고 있어 한가지 더 조건이 필요하여 추가.
    // pathName url 이 변경되는 시간보다 pageType 변경되는 시간이 늦어 경기결과에서 뒤로가기시 토너먼트 트랙을 2번 호출하는 문제가 생김.
    if (pageType === PAGE_TYPE.MATCHUP_RESULT && pathName !== '/matchupResult') {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {}
    }

    if (pageType !== PAGE_TYPE.MATCHUP_RESULT && pathName === '/matchupResult') {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {}
    }

    if (pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {}
    }

    let limit = 20
    if (currentGameKey === GAME_TYPE.LIVE) {
      limit = 999
    }

    if (club.state === CLUB_STATE.RUNNING) {
      if (currentGameKey === GAME_TYPE.LIVE) {
        if (isAvailableLiveMatch(id, currentGameKey)) {
          matchupStore.fetchTournamentTracks({ limit, reset: true, pageType }).catch(err => console.error(err))
          if (intervalObj) {
            clearInterval(intervalObj)
          }
          intervalObj = setInterval(() => {
            matchupStore.fetchTournamentTracks({ limit, reset: false, pageType }).catch(err => console.error(err))
          }, 10000)
        }
      } else {
        matchupStore.fetchTournamentTracks({ limit, reset: true, pageType }).catch(err => console.error(err))
      }
    } else if (pageType === PAGE_TYPE.MATCHUP_RESULT && !isLoading) {
      matchupStore.fetchTournamentTracks({ limit, reset: true, pageType }).catch(err => console.error(err))
    }
    return () => {
      if (intervalObj) {
        clearInterval(intervalObj)
      }
    }
  }, [id, currentSportsKey, majorUniqueID, pageType, scoreModeEnabled, selectedDetailTabs])

  const viewKey = 'PreMatchPage:PreMatchList'
  return <PreMatchList key={viewKey} pageType={pageType} tracks={tracks} onFieldClickCheck={onFieldClickCheck}/>
}

export default observer(PreMatchPage)
